import { DateTime } from 'luxon'
import secureStorageService from './secureStorageService'

// Generic cache entry interface
interface CacheEntry<T = any> {
  id: string
  data: T
  metadata: {
    cachedAt: string
    expiresAt?: string
    lastFetch?: string
    totalCount?: number
    isStale?: boolean
    [key: string]: any // Allow additional metadata fields
  }
}

// Encrypted cache entry
interface EncryptedCacheEntry {
  id: string
  encryptedData: string
  metadata: CacheEntry['metadata']
}

// Cache options for operations
interface CacheOptions {
  encrypt?: boolean
  ttlSeconds?: number
  namespace?: string
  searchFields?: string[]
}

// Query options for loading data
interface QueryOptions {
  limit?: number
  offset?: number
  searchTerm?: string
  searchFields?: string[]
  sortField?: string
  sortDirection?: 'asc' | 'desc'
  filters?: Record<string, any>
}

export class IndexedDbService {
  private db: IDBDatabase | null = null
  private readonly DB_NAME = 'TalkingParentsApp'
  private readonly DB_VERSION = 2 // Increment version for schema changes
  private readonly CACHE_STORE = 'cache_data'

  // Safari detection and configuration
  private readonly isSafari = this.detectSafari()
  private readonly safariTimeoutMs = 5000
  private readonly defaultTimeoutMs = 2000
  private readonly maxRetries = this.isSafari ? 3 : 1
  private readonly baseRetryDelay = 1000

  // Connection health tracking
  private visibilityChangeHandler: (() => void) | null = null
  private isConnectionHealthy = true

  private detectSafari(): boolean {
    const userAgent = navigator.userAgent.toLowerCase()
    return (
      /safari/.test(userAgent) &&
      !/chrome/.test(userAgent) &&
      !/chromium/.test(userAgent)
    )
  }

  /**
   * Check if IndexedDB is supported in this browser
   */
  static isSupported(): boolean {
    try {
      return !!(
        typeof indexedDB !== 'undefined' &&
        indexedDB &&
        typeof indexedDB.open === 'function'
      )
    } catch {
      return false
    }
  }

  /**
   * Check if IndexedDB is available (not disabled in private browsing)
   */
  static async isAvailable(): Promise<boolean> {
    if (!this.isSupported()) {
      return false
    }

    try {
      const instance = new IndexedDbService()
      const timeout = instance.isSafari
        ? instance.safariTimeoutMs
        : instance.defaultTimeoutMs

      const testDB = await new Promise<boolean>((resolve) => {
        const request = indexedDB.open('__tp_availability_test__', 1)
        let resolved = false

        const cleanup = () => {
          if (!resolved) {
            resolved = true
            resolve(false)
          }
        }

        const success = () => {
          if (!resolved) {
            resolved = true
            try {
              request.result.close()
              indexedDB.deleteDatabase('__tp_availability_test__')
            } catch {
              // Ignore cleanup errors
            }
            resolve(true)
          }
        }

        request.onerror = cleanup
        request.onblocked = cleanup
        request.onsuccess = success
        setTimeout(cleanup, timeout)
      })

      return testDB
    } catch {
      return false
    }
  }

  /**
   * Initialize the IndexedDB service
   */
  async initialize(userIdentifier?: string): Promise<void> {
    if (!IndexedDbService.isSupported()) {
      throw new Error('IndexedDB is not supported in this browser')
    }

    if (!(await IndexedDbService.isAvailable())) {
      throw new Error(
        'IndexedDB is not available (possibly disabled in private browsing)'
      )
    }

    if (this.isSafari) {
      this.setupVisibilityChangeHandling()
    }

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.DB_NAME, this.DB_VERSION)

      request.onerror = () => {
        this.isConnectionHealthy = false
        reject(
          new Error(
            `IndexedDB initialization failed: ${request.error?.message || 'Unknown error'}`
          )
        )
      }

      request.onsuccess = async () => {
        this.db = request.result
        this.isConnectionHealthy = true

        if (this.isSafari && this.db) {
          this.db.addEventListener('close', () => {
            console.warn(
              '[IndexedDbService] Safari IndexedDB connection closed unexpectedly'
            )
            this.isConnectionHealthy = false
          })
        }

        if (userIdentifier) {
          try {
            await secureStorageService.initializeEncryption(userIdentifier)
          } catch {
            // Encryption initialization failed, continue without encryption
          }
        }

        resolve()
      }

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result

        // Create or upgrade the cache store
        if (!db.objectStoreNames.contains(this.CACHE_STORE)) {
          const cacheStore = db.createObjectStore(this.CACHE_STORE, {
            keyPath: 'id'
          })

          // Create indexes for efficient querying
          cacheStore.createIndex('cachedAt', 'metadata.cachedAt', {
            unique: false
          })
          cacheStore.createIndex('expiresAt', 'metadata.expiresAt', {
            unique: false
          })
          cacheStore.createIndex('namespace', 'namespace', { unique: false })
          cacheStore.createIndex('isStale', 'metadata.isStale', {
            unique: false
          })
        }

        // Remove old stores if they exist (migration from journal-specific implementation)
        const oldStores = ['journal_entries', 'journal_metadata']
        oldStores.forEach((storeName) => {
          if (db.objectStoreNames.contains(storeName)) {
            db.deleteObjectStore(storeName)
          }
        })
      }
    })
  }

  /**
   * Save data to cache with optional encryption
   */
  async set<T>(
    key: string,
    data: T,
    options: CacheOptions = {}
  ): Promise<void> {
    return this.withRetry(async () => {
      this.ensureInitialized()

      const { encrypt = false, ttlSeconds, namespace = 'default' } = options

      const now = DateTime.now()
      const id = this.buildCacheKey(namespace, key)

      const metadata = {
        cachedAt: now.toISO(),
        expiresAt: ttlSeconds
          ? now.plus({ seconds: ttlSeconds }).toISO()
          : undefined,
        lastFetch: now.toISO(),
        isStale: false
      }

      let cacheEntry: CacheEntry<T> | EncryptedCacheEntry

      if (encrypt && secureStorageService.isEncryptionReady()) {
        const encryptedData = await secureStorageService.encryptData(data)
        cacheEntry = { id, encryptedData, metadata } as EncryptedCacheEntry
      } else {
        cacheEntry = { id, data, metadata } as CacheEntry<T>
      }

      const transaction = this.db!.transaction([this.CACHE_STORE], 'readwrite')
      const store = transaction.objectStore(this.CACHE_STORE)

      await this.promisifyRequest(
        store.put({
          ...cacheEntry,
          namespace // Add namespace for indexing
        })
      )

      await this.promisifyTransaction(transaction)
    }, `set(${key})`)
  }

  /**
   * Get data from cache with optional decryption
   */
  async get<T>(
    key: string,
    options: { namespace?: string; decrypt?: boolean } = {}
  ): Promise<T | null> {
    return this.withRetry(async () => {
      this.ensureInitialized()

      const { namespace = 'default', decrypt = false } = options
      const id = this.buildCacheKey(namespace, key)

      const transaction = this.db!.transaction([this.CACHE_STORE], 'readonly')
      const store = transaction.objectStore(this.CACHE_STORE)

      const result = await this.promisifyRequest(store.get(id))

      if (!result) {
        return null
      }

      // Check if entry has expired
      if (result.metadata.expiresAt) {
        const expiresAt = DateTime.fromISO(result.metadata.expiresAt)
        if (DateTime.now() > expiresAt) {
          // Entry has expired, delete it and return null
          await this.delete(key, { namespace })
          return null
        }
      }

      // Handle encrypted data
      if (
        decrypt &&
        'encryptedData' in result &&
        secureStorageService.isEncryptionReady()
      ) {
        try {
          return await secureStorageService.decryptData<T>(result.encryptedData)
        } catch {
          return null
        }
      }

      // Return regular data
      return result.data || null
    }, `get(${key})`)
  }

  /**
   * Query cache entries with filtering and pagination
   */
  async query<T>(
    options: QueryOptions & { namespace?: string } = {}
  ): Promise<{ entries: T[]; totalCount: number; hasMore: boolean }> {
    return this.withRetry(async () => {
      this.ensureInitialized()

      const {
        limit,
        offset = 0,
        searchTerm,
        searchFields = [],
        namespace = 'default',
        filters = {}
      } = options

      const transaction = this.db!.transaction([this.CACHE_STORE], 'readonly')
      const store = transaction.objectStore(this.CACHE_STORE)
      const index = store.index('namespace')

      let allEntries: any[] = []

      // Get all entries for the namespace
      const request = index.getAll(namespace)
      const results = await this.promisifyRequest(request)

      // Filter out expired entries and apply filters
      for (const result of results) {
        // Check expiration
        if (result.metadata.expiresAt) {
          const expiresAt = DateTime.fromISO(result.metadata.expiresAt)
          if (DateTime.now() > expiresAt) {
            continue // Skip expired entries
          }
        }

        // Decrypt data if needed
        let data = result.data
        if (
          'encryptedData' in result &&
          secureStorageService.isEncryptionReady()
        ) {
          try {
            data = await secureStorageService.decryptData(result.encryptedData)
          } catch {
            continue // Skip entries that can't be decrypted
          }
        }

        // Apply search filter
        if (searchTerm && searchFields.length > 0) {
          const searchLower = searchTerm.toLowerCase()
          const matchesSearch = searchFields.some((field) => {
            const fieldValue = this.getNestedValue(data, field)
            return (
              fieldValue &&
              String(fieldValue).toLowerCase().includes(searchLower)
            )
          })

          if (!matchesSearch) {
            continue
          }
        }

        // Apply custom filters
        let matchesFilters = true
        for (const [filterKey, filterValue] of Object.entries(filters)) {
          const dataValue = this.getNestedValue(data, filterKey)
          if (dataValue !== filterValue) {
            matchesFilters = false
            break
          }
        }

        if (matchesFilters) {
          allEntries.push(data)
        }
      }

      const totalCount = allEntries.length

      // Apply pagination
      const entries = limit
        ? allEntries.slice(offset, offset + limit)
        : allEntries.slice(offset)

      const hasMore = limit ? offset + limit < totalCount : false

      return { entries, totalCount, hasMore }
    }, 'query')
  }

  /**
   * Delete a cache entry
   */
  async delete(
    key: string,
    options: { namespace?: string } = {}
  ): Promise<void> {
    return this.withRetry(async () => {
      this.ensureInitialized()

      const { namespace = 'default' } = options
      const id = this.buildCacheKey(namespace, key)

      const transaction = this.db!.transaction([this.CACHE_STORE], 'readwrite')
      const store = transaction.objectStore(this.CACHE_STORE)

      await this.promisifyRequest(store.delete(id))
      await this.promisifyTransaction(transaction)
    }, `delete(${key})`)
  }

  /**
   * Clear all cache entries for a namespace
   */
  async clearNamespace(namespace: string = 'default'): Promise<void> {
    return this.withRetry(async () => {
      this.ensureInitialized()

      const transaction = this.db!.transaction([this.CACHE_STORE], 'readwrite')
      const store = transaction.objectStore(this.CACHE_STORE)
      const index = store.index('namespace')

      const request = index.getAllKeys(namespace)
      const keys = await this.promisifyRequest(request)

      for (const key of keys) {
        await this.promisifyRequest(store.delete(key))
      }

      await this.promisifyTransaction(transaction)
    }, `clearNamespace(${namespace})`)
  }

  /**
   * Clear all expired entries
   */
  async clearExpired(): Promise<number> {
    return this.withRetry(async () => {
      this.ensureInitialized()

      const now = DateTime.now().toISO()
      const transaction = this.db!.transaction([this.CACHE_STORE], 'readwrite')
      const store = transaction.objectStore(this.CACHE_STORE)
      const index = store.index('expiresAt')

      // Get all entries with expiration times less than now
      const range = IDBKeyRange.upperBound(now)
      const request = index.openCursor(range)

      let deletedCount = 0

      return new Promise<number>((resolve, reject) => {
        request.onsuccess = (event) => {
          const cursor = (event.target as IDBRequest).result
          if (cursor) {
            cursor.delete()
            deletedCount++
            cursor.continue()
          } else {
            resolve(deletedCount)
          }
        }

        request.onerror = () => reject(request.error)
      })
    }, 'clearExpired')
  }

  /**
   * Get cache statistics
   */
  async getStats(): Promise<{
    totalEntries: number
    entriesByNamespace: Record<string, number>
    expiredEntries: number
    totalSize: number // Approximate size in bytes
  }> {
    this.ensureInitialized()

    const transaction = this.db!.transaction([this.CACHE_STORE], 'readonly')
    const store = transaction.objectStore(this.CACHE_STORE)

    const allEntries = await this.promisifyRequest(store.getAll())
    const now = DateTime.now()

    const stats = {
      totalEntries: allEntries.length,
      entriesByNamespace: {} as Record<string, number>,
      expiredEntries: 0,
      totalSize: 0
    }

    for (const entry of allEntries) {
      // Count by namespace
      const namespace = entry.namespace || 'default'
      stats.entriesByNamespace[namespace] =
        (stats.entriesByNamespace[namespace] || 0) + 1

      // Count expired entries
      if (entry.metadata.expiresAt) {
        const expiresAt = DateTime.fromISO(entry.metadata.expiresAt)
        if (now > expiresAt) {
          stats.expiredEntries++
        }
      }

      // Approximate size calculation
      stats.totalSize += JSON.stringify(entry).length * 2 // Rough estimate
    }

    return stats
  }

  /**
   * Get data AND metadata from cache without removing expired entries.
   * Use this from repositories that need to implement custom staleness logic.
   */
  async getWithMeta<T>(
    key: string,
    options: { namespace?: string; decrypt?: boolean } = {}
  ): Promise<{ data: T | null; metadata: CacheEntry['metadata'] } | null> {
    return this.withRetry(async () => {
      this.ensureInitialized()

      const { namespace = 'default', decrypt = false } = options
      const id = this.buildCacheKey(namespace, key)

      const transaction = this.db!.transaction([this.CACHE_STORE], 'readonly')
      const store = transaction.objectStore(this.CACHE_STORE)

      const raw = await this.promisifyRequest(store.get(id))

      if (!raw) return null

      // Decrypt data if needed
      let data: T | null = raw.data ?? null

      if (
        decrypt &&
        'encryptedData' in raw &&
        secureStorageService.isEncryptionReady()
      ) {
        try {
          data = await secureStorageService.decryptData<T>(raw.encryptedData)
        } catch {
          // decryption failed -> treat as no data
          data = null
        }
      }

      return { data, metadata: raw.metadata }
    }, `getWithMeta(${key})`)
  }

  // Private helper methods

  private buildCacheKey(namespace: string, key: string): string {
    return `${namespace}:${key}`
  }

  private getNestedValue(obj: unknown, path: string): unknown {
    return path.split('.').reduce((current: any, key) => current?.[key], obj)
  }

  private setupVisibilityChangeHandling(): void {
    if (this.visibilityChangeHandler) {
      return
    }

    this.visibilityChangeHandler = async () => {
      if (document.visibilityState === 'visible' && this.isSafari) {
        if (!this.isConnectionHealthy || !this.db) {
          console.info(
            '[IndexedDbService] Attempting to restore Safari IndexedDB connection after visibility change'
          )
          try {
            await this.reinitializeConnection()
          } catch (error) {
            console.error(
              '[IndexedDbService] Failed to restore connection:',
              error
            )
          }
        }
      }
    }

    document.addEventListener('visibilitychange', this.visibilityChangeHandler)
  }

  private async reinitializeConnection(): Promise<void> {
    try {
      if (this.db) {
        this.db.close()
        this.db = null
      }

      await new Promise((resolve) => setTimeout(resolve, 100))
      await this.initialize()

      console.info(
        '[IndexedDbService] Safari IndexedDB connection restored successfully'
      )
    } catch (error) {
      console.error(
        '[IndexedDbService] Failed to reinitialize Safari IndexedDB connection:',
        error
      )
      throw error
    }
  }

  private ensureInitialized(): void {
    if (!this.db) {
      throw new Error('IndexedDB not initialized. Call initialize() first.')
    }

    if (this.isSafari && !this.isConnectionHealthy) {
      throw new Error(
        'IndexedDB connection is not healthy. Reconnection required.'
      )
    }
  }

  private async withRetry<T>(
    operation: () => Promise<T>,
    operationName: string
  ): Promise<T> {
    let lastError: Error | null = null

    for (let attempt = 0; attempt <= this.maxRetries; attempt++) {
      try {
        return await operation()
      } catch (error) {
        lastError = error as Error

        const isSafariConnectionError =
          this.isSafari &&
          error instanceof Error &&
          (error.message.includes(
            'Connection to Indexed Database server lost'
          ) ||
            error.message.includes('database connection is closing') ||
            error.message.includes(
              'The operation failed for reasons unrelated to the database'
            ))

        if (isSafariConnectionError && attempt < this.maxRetries) {
          console.warn(
            `[IndexedDbService] ${operationName} failed (attempt ${attempt + 1}/${this.maxRetries + 1}), retrying...`,
            error
          )

          const delay =
            this.baseRetryDelay * Math.pow(2, attempt) + Math.random() * 1000
          await new Promise((resolve) => setTimeout(resolve, delay))

          if (!this.db) {
            try {
              await this.initialize()
            } catch (reinitError) {
              console.warn(
                '[IndexedDbService] Failed to reinitialize connection:',
                reinitError
              )
            }
          }
          continue
        }

        throw error
      }
    }

    throw lastError
  }

  private promisifyRequest<T>(request: IDBRequest<T>): Promise<T> {
    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => {
        const error = request.error

        if (this.isSafari && error) {
          if (
            error.message?.includes('database connection is closing') ||
            error.message?.includes(
              'Connection to Indexed Database server lost'
            )
          ) {
            this.isConnectionHealthy = false
          }
        }

        reject(error)
      }
    })
  }

  private promisifyTransaction(transaction: IDBTransaction): Promise<void> {
    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve()
      transaction.onerror = () => {
        const error = transaction.error

        if (this.isSafari && error) {
          console.warn(
            '[IndexedDbService] Safari transaction failed:',
            error.message
          )

          if (
            error.message?.includes('database connection is closing') ||
            error.message?.includes(
              'Connection to Indexed Database server lost'
            )
          ) {
            this.isConnectionHealthy = false
          }
        }

        reject(error)
      }
      transaction.onabort = () => {
        console.warn('[IndexedDbService] Transaction was aborted')
        reject(new Error('Transaction was aborted'))
      }
    })
  }

  // Backward compatibility methods for Journal (to be migrated to generic methods)

  /**
   * @deprecated Use generic methods instead: get('entries', { namespace: 'journal', decrypt: true })
   */
  async loadJournalEntries(
    _options: { limit?: number } = {}
  ): Promise<{ entries: any[]; metadata: any }> {
    try {
      // Load entries
      const entries =
        ((await this.get('entries', {
          namespace: 'journal',
          decrypt: true
        })) as any[]) || []

      // Load metadata
      const metadata = await this.get('metadata', {
        namespace: 'journal',
        decrypt: false
      })

      return { entries, metadata }
    } catch (error) {
      console.warn('[IndexedDbService] loadJournalEntries fallback:', error)
      return { entries: [], metadata: null }
    }
  }

  /**
   * @deprecated Use generic methods instead: set('entries', data) and set('metadata', meta)
   */
  async saveJournalEntries(entries: any[], metadata: any): Promise<void> {
    try {
      // Save entries with encryption
      await this.set('entries', entries, {
        namespace: 'journal',
        encrypt: true,
        ttlSeconds: 30 * 60 // 30 minutes
      })

      // Save metadata
      await this.set('metadata', metadata, {
        namespace: 'journal',
        encrypt: false,
        ttlSeconds: 30 * 60
      })
    } catch (error) {
      console.warn('[IndexedDbService] saveJournalEntries fallback:', error)
      // Fail silently for backward compatibility
    }
  }

  /**
   * @deprecated Use generic method instead: clearNamespace('journal')
   */
  async clearJournalCache(): Promise<void> {
    try {
      await this.clearNamespace('journal')
    } catch (error) {
      console.warn('[IndexedDbService] clearJournalCache fallback:', error)
      // Fail silently for backward compatibility
    }
  }

  /**
   * @deprecated Use generic method instead: get('metadata', { namespace: 'journal' })
   */
  async getJournalMetadata(): Promise<any> {
    try {
      return await this.get('metadata', {
        namespace: 'journal',
        decrypt: false
      })
    } catch (error) {
      console.warn('[IndexedDbService] getJournalMetadata fallback:', error)
      return null
    }
  }

  /**
   * Clean up event listeners and close connection
   */
  close(): void {
    if (this.visibilityChangeHandler) {
      document.removeEventListener(
        'visibilitychange',
        this.visibilityChangeHandler
      )
      this.visibilityChangeHandler = null
    }

    if (this.db) {
      this.db.close()
      this.db = null
    }

    this.isConnectionHealthy = false
  }
}

// Create singleton instance
export const indexedDbService = new IndexedDbService()
export default indexedDbService
